import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  Clock,
  Loader2,
  TrendingUp,
  XCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";

interface Counts {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

/**
 * /dashboard (admin) landing page. High-signal summary plus the three
 * most actionable things an admin might do right now. Deeper tabs
 * handle the actual work.
 */
export function AdminOverview() {
  const { profile } = useAuth();
  const [counts, setCounts] = useState<Counts | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("venue_applications")
        .select("status");
      if (cancelled || !data) return;
      const c: Counts = { pending: 0, approved: 0, rejected: 0, total: 0 };
      for (const row of data) {
        c.total++;
        if (row.status === "pending") c.pending++;
        else if (row.status === "approved") c.approved++;
        else if (row.status === "rejected") c.rejected++;
      }
      setCounts(c);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <header className="mb-8">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">
          Hey{profile?.display_name ? `, ${profile.display_name.split(" ")[0]}` : ""}.
        </h1>
        <p className="mt-2 text-muted-foreground">
          Here's what's worth looking at right now.
        </p>
      </header>

      {/* Stat row - each card links through to the Venues tab with
          the matching status filter pre-applied (via ?status= query
          param). */}
      <section className="mb-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          to="/dashboard/venues?status=pending"
          icon={<Clock className="h-4 w-4" />}
          label="Pending"
          value={counts?.pending ?? "-"}
          tone="volt"
        />
        <Stat
          to="/dashboard/venues?status=approved"
          icon={<CheckCircle2 className="h-4 w-4" />}
          label="Approved"
          value={counts?.approved ?? "-"}
        />
        <Stat
          to="/dashboard/venues?status=rejected"
          icon={<XCircle className="h-4 w-4" />}
          label="Rejected"
          value={counts?.rejected ?? "-"}
        />
        <Stat
          to="/dashboard/venues?status=all"
          icon={<Building2 className="h-4 w-4" />}
          label="Total apps"
          value={counts?.total ?? "-"}
        />
      </section>

      {/* Quick actions */}
      <section>
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Jump in
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <ActionCard
            to="/dashboard/venues"
            icon={<Building2 className="h-5 w-5" />}
            title="Review venue applications"
            body={
              counts === null
                ? "Loading…"
                : counts.pending === 0
                ? "No pending applications."
                : `${counts.pending} waiting on approval.`
            }
            accent={(counts?.pending ?? 0) > 0}
          />
          <ActionCard
            to="/dashboard/announcements"
            icon={<TrendingUp className="h-5 w-5" />}
            title="Post an announcement"
            body="Push a banner or notification to players and venues."
          />
        </div>
      </section>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  tone,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  tone?: "volt";
  to?: string;
}) {
  const inner = (
    <>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <span className={tone === "volt" ? "text-volt" : undefined}>{icon}</span>
        {label}
      </div>
      <p className="text-2xl font-black tracking-tight">
        {value === null ? <Loader2 className="h-5 w-5 animate-spin" /> : value}
      </p>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className="block rounded-xl border border-border bg-card p-4 transition-colors hover:border-volt/60"
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-card p-4">{inner}</div>
  );
}

function ActionCard({
  to,
  icon,
  title,
  body,
  accent,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  body: string;
  accent?: boolean;
}) {
  return (
    <Link
      to={to}
      className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-volt/60"
    >
      <span
        className={
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg " +
          (accent
            ? "bg-volt text-primary-foreground"
            : "bg-volt-subtle text-volt")
        }
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="font-semibold leading-tight">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{body}</p>
      </div>
    </Link>
  );
}
